import { useNavigate } from 'react-router-dom'
import CurvePriceChart from '@/components/CurvePriceChart'
import ExplorerLink from '@/components/ExplorerLink'
import WatchButton from '@/components/WatchButton'
import {
  chartPointsForLaunch,
  isLaunchGraduated,
  launchGraduationProgress,
  launchGraduationQuoteEth,
  marketCapWei,
  spotPriceWei,
} from '@/lib/launchpad/curveMath'
import type { LaunchState } from '@/lib/launchpad/types'
import { shortenAddress } from '@/lib/launchpad/contracts'
import { formatCompactEth } from '@/lib/launchpad/wei'
import { formatUsdFromEthWei } from '@/lib/ethUsd'
import { useEthUsdPrice } from '@/hooks/useEthUsdPrice'
import { useTokenImage } from '@/lib/projectLinks'
import { tokenSharePath } from '@/lib/shareSlug'

function SimLaunchCard({ launch }: { launch: LaunchState }) {
  const imageUrl = useTokenImage([launch.contractAddress, launch.shareSlug, launch.id], launch.imageUrl)
  const raised = formatCompactEth(launch.realQuoteRaised)
  const graduationEth = launchGraduationQuoteEth(launch)
  const progress = launchGraduationProgress(launch)
  const price = formatCompactEth(spotPriceWei(launch))
  const ethUsd = useEthUsdPrice()
  const mcap = formatUsdFromEthWei(marketCapWei(launch), ethUsd)
  const graduated = isLaunchGraduated(launch)
  const navigate = useNavigate()
  const detailPath = tokenSharePath(launch.shareSlug, launch.id)
  const chartPoints = chartPointsForLaunch(launch)

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={() => navigate(detailPath)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          navigate(detailPath)
        }
      }}
      aria-label={`View ${launch.name} launch details`}
      className={`group flex cursor-pointer flex-col overflow-hidden rounded-2xl border backdrop-blur-md transition-colors sm:rounded-3xl ${graduated ? 'border-lime-400/40 bg-lime-400/[0.06] hover:border-lime-300/60' : 'border-white/10 bg-white/[0.03] hover:border-lime-400/30'}`}
    >
      <div className="relative aspect-square w-full overflow-hidden bg-zinc-950 sm:aspect-[4/3]">
        {imageUrl
          ? (
              <img
                src={imageUrl}
                alt={`${launch.name} token art`}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
              />
            )
          : (
              <div className="flex h-full w-full items-center justify-center border-b border-lime-400/20 bg-lime-400/10 text-xl font-bold text-lime-400 sm:text-3xl">
                {launch.ticker.slice(0, 2)}
              </div>
            )}
        <div className="absolute right-2 top-2 sm:right-3 sm:top-3">
          <WatchButton projectId={launch.id} projectName={launch.name} compact />
        </div>
        <span className={`absolute bottom-2 left-2 inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] sm:bottom-3 sm:left-3 sm:px-3 sm:py-1 sm:text-xs sm:tracking-[0.18em] ${
          graduated ? 'bg-lime-400 text-black' : 'bg-black/70 text-lime-400 backdrop-blur-sm'
        }`}
        >
          {graduated ? 'Graduated' : 'On curve'}
        </span>
      </div>
      <div className="p-3 sm:p-5">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-white sm:text-base">{launch.name}</h3>
          <p className="mt-0.5 truncate text-[11px] text-zinc-500 sm:text-xs">
            $
            {launch.ticker}
            {' '}
            · {shortenAddress(launch.creatorAddress)}
          </p>
          {launch.contractAddress
            ? (
                <div className="mt-1 hidden sm:block">
                  <ExplorerLink address={launch.contractAddress} label="On-chain" />
                </div>
              )
            : null}
        </div>
        {chartPoints.length > 1
          ? (
              <div className="mb-3 mt-3 hidden sm:block">
                <CurvePriceChart points={chartPoints} compact height={72} unit="eth" showGraduation />
              </div>
            )
          : null}
        <div className={`${chartPoints.length > 1 ? 'mt-2 sm:mt-0' : 'mt-2 sm:mt-3 '}h-1 overflow-hidden rounded-full bg-zinc-900 sm:h-1.5`} role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100} aria-label="Graduation progress">
          <div className="h-full rounded-full bg-lime-400" style={{ width: `${progress}%` }} />
        </div>
        <p className="mt-1.5 truncate text-[10px] text-zinc-500 sm:mt-2 sm:text-[11px]">
          {raised}
          {' '}
          /
          {' '}
          {graduationEth}
          {' '}
          ETH
        </p>
        <div className="mt-2 hidden grid-cols-2 gap-2 text-[11px] sm:mt-3 sm:grid">
          <div>
            <p className="text-zinc-500">Price</p>
            <p className="font-semibold text-white">
              {price}
              {' '}
              ETH
            </p>
          </div>
          <div>
            <p className="text-zinc-500">Mcap</p>
            <p className="font-semibold text-white">{mcap}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SimLaunchCard
